import { motion } from 'framer-motion';

interface AIAnswerBlockProps {
  context?: 'home' | 'services' | 'about' | 'locations';
}

const content = {
  home: {
    headline: 'Everything You Need to Know About Signarama Arlington',
    subheadline:
      'Structured information designed to answer your most important questions instantly.',
    cards: [
      {
        icon: 'ri-tools-line',
        color: 'from-teal-500 to-cyan-500',
        question: 'What signage services does Signarama Arlington offer?',
        answer:
          'Signarama Arlington provides vehicle wraps and fleet graphics, storefront channel letters, illuminated signs, banners and displays, trade show exhibit graphics, window and floor decals, wayfinding systems, and promotional signage for businesses of all sizes in Arlington, TX and the DFW Metroplex.',
      },
      {
        icon: 'ri-user-star-line',
        color: 'from-orange-500 to-red-400',
        question: 'Who is Signarama Arlington best for?',
        answer:
          'We serve retail businesses, restaurants, auto dealerships, medical offices, real estate agencies, construction companies, event organizers, and any local business looking to increase visibility and brand recognition in the Arlington, Grand Prairie, Fort Worth, and Mansfield areas.',
      },
      {
        icon: 'ri-checkbox-circle-line',
        color: 'from-emerald-500 to-teal-500',
        question: 'What are the key benefits of working with us?',
        answer:
          'Clients benefit from 40+ years of Signarama franchise expertise, premium-grade materials with UV and weather resistance, in-house design and production, professional installation, fast same-week turnaround on most projects, and a single partner for concept through completion.',
      },
      {
        icon: 'ri-map-pin-line',
        color: 'from-amber-500 to-orange-500',
        question: 'Where is Signarama Arlington located and who do they serve?',
        answer:
          'Located at 2400 W Pioneer Pkwy #118, Arlington, Texas 76013, we serve the entire DFW Metroplex including Arlington, Grand Prairie, Mansfield, Fort Worth, Irving, Euless, Bedford, Hurst, and surrounding communities. Call us at 817-860-9310.',
      },
    ],
  },
  services: {
    headline: 'Your Questions About Our Signage Services — Answered',
    subheadline:
      'Clear, direct answers to help you choose the right signage solution for your business.',
    cards: [
      {
        icon: 'ri-car-line',
        color: 'from-teal-500 to-cyan-500',
        question: 'How long do vehicle wraps last?',
        answer:
          'High-quality vehicle wraps installed by Signarama Arlington typically last 5–7 years with proper care. We use 3M and Avery Dennison premium vinyl materials that resist UV fading, weather, and daily wear while maintaining vibrant colors and sharp graphics throughout their lifespan.',
      },
      {
        icon: 'ri-store-2-line',
        color: 'from-orange-500 to-red-400',
        question: 'What types of storefront signs does Signarama make?',
        answer:
          'We produce illuminated channel letters, cabinet signs, dimensional lettering, ACM panel signs, LED monument signs, window graphics, and ADA-compliant directional signs. Every storefront sign is custom-designed to match your brand identity and local zoning requirements.',
      },
      {
        icon: 'ri-presentation-line',
        color: 'from-emerald-500 to-teal-500',
        question: 'What trade show display options are available?',
        answer:
          'Signarama Arlington offers retractable banners, fabric pop-up displays, tension fabric backdrops, tabletop displays, modular exhibit systems, printed table covers, and hanging banners. All displays are designed for portability, professional appearance, and repeated use at events.',
      },
      {
        icon: 'ri-time-line',
        color: 'from-amber-500 to-orange-500',
        question: 'How quickly can Signarama Arlington complete my sign project?',
        answer:
          'Most standard sign orders are completed within 3–7 business days. Rush production with same-week turnaround is available for qualifying projects. Complex installations like illuminated channel letters or large-format displays may require 2–3 weeks including permit coordination.',
      },
    ],
  },
  about: {
    headline: 'About Signarama Arlington — Local Experts, Global Strength',
    subheadline:
      'Your questions about who we are, our experience, and why we are the right choice.',
    cards: [
      {
        icon: 'ri-history-line',
        color: 'from-teal-500 to-cyan-500',
        question: 'How long has Signarama been in business?',
        answer:
          'Signarama was founded in 1986 and has grown to 900+ locations worldwide, making it the world\'s largest sign franchise. The Arlington, TX location brings this global expertise and proven systems directly to DFW businesses, combining decades of innovation with personalized local service.',
      },
      {
        icon: 'ri-team-line',
        color: 'from-orange-500 to-red-400',
        question: 'Who owns and operates Signarama Arlington?',
        answer:
          'Signarama Arlington is locally owned and operated, serving the Arlington and DFW community with deep roots in the area. Our team includes certified sign designers, production specialists, and professional installers committed to delivering exceptional results for every client.',
      },
      {
        icon: 'ri-leaf-line',
        color: 'from-emerald-500 to-teal-500',
        question: 'What materials and technology does Signarama Arlington use?',
        answer:
          'We use industry-leading materials including 3M and Avery Dennison vinyl, LED lighting systems, aluminum composites, acrylic, and HDU foam. Our facility features state-of-the-art wide-format printers and CNC routers ensuring precise, high-quality output on every project.',
      },
      {
        icon: 'ri-heart-line',
        color: 'from-amber-500 to-orange-500',
        question: 'Is Signarama Arlington involved in the local community?',
        answer:
          'Yes — we are proud supporters of Arlington-area businesses, events, and organizations. From sponsoring local events to creating signage for nonprofits and schools, Signarama Arlington is committed to helping our community thrive through the power of visual communication.',
      },
    ],
  },
  locations: {
    headline: 'Signarama Arlington — Location, Hours & Service Territory',
    subheadline:
      'Everything you need to find us, contact us, and understand where we provide services.',
    cards: [
      {
        icon: 'ri-map-pin-2-line',
        color: 'from-teal-500 to-cyan-500',
        question: 'Where is Signarama Arlington located?',
        answer:
          'Signarama Arlington is located at 2400 W Pioneer Pkwy, Suite 118, Arlington, Texas 76013. We are situated in the heart of Arlington, easily accessible from I-20 and Highway 360. Free on-site parking is available. Call 817-860-9310 to schedule a consultation.',
      },
      {
        icon: 'ri-time-line',
        color: 'from-orange-500 to-red-400',
        question: 'What are Signarama Arlington\'s business hours?',
        answer:
          'Signarama Arlington is open Monday through Friday from 8:00 AM to 6:00 PM. We are closed on weekends and major holidays. Same-week appointments are available. You can also request a quote online 24/7 through our website and we will respond the next business day.',
      },
      {
        icon: 'ri-road-map-line',
        color: 'from-emerald-500 to-teal-500',
        question: 'What cities does Signarama Arlington serve?',
        answer:
          'We serve all of Tarrant and Dallas County including Arlington, Grand Prairie, Mansfield, Fort Worth, Irving, Euless, Bedford, Hurst, Kennedale, Pantego, and North Arlington. On-site consultations and installations are available throughout the DFW Metroplex.',
      },
      {
        icon: 'ri-phone-line',
        color: 'from-amber-500 to-orange-500',
        question: 'How can I contact Signarama Arlington?',
        answer:
          'Reach us by phone at 817-860-9310 during business hours for immediate assistance. For project quotes, use our online form and we\'ll respond within 2–4 business hours. Visit us in-person at 2400 W Pioneer Pkwy #118, Arlington, TX for a free walk-in consultation.',
      },
    ],
  },
};

export function AIAnswerBlock({ context = 'home' }: AIAnswerBlockProps) {
  const { headline, subheadline, cards } = content[context];

  return (
    <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-teal-50 border border-teal-200 rounded-full px-4 py-2 mb-6">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-sparkling-line text-teal-600 text-sm"></i>
            </div>
            <span className="text-teal-700 text-xs font-semibold tracking-wide uppercase">
              AI-Optimized Information
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{headline}</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">{subheadline}</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {cards.map((card, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-2xl p-8 border border-gray-100 hover:border-teal-200 transition-all"
            >
              <div className="flex items-start space-x-4">
                <div
                  className={`w-12 h-12 bg-gradient-to-br ${card.color} rounded-xl flex items-center justify-center flex-shrink-0`}
                >
                  <i className={`${card.icon} text-xl text-white`}></i>
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-2">{card.question}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{card.answer}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
